/** Mock mapping of industry → onboarding questions (stands in for the
 *  `industry_onboarding_questions` Firestore collection). */

export type QuestionType = 'text' | 'textarea' | 'select' | 'number' | 'boolean' | 'multiselect';

export interface OnboardingQuestion {
  fieldName: string;
  label: string;
  type: QuestionType;
  options?: string[];
  required: boolean;
  placeholder?: string;
}

/** Common fields asked of every company (name comes from checkout). */
const COMMON: OnboardingQuestion[] = [
  { fieldName: 'description', label: 'Company description', type: 'textarea', required: true, placeholder: 'What does your company do?' },
  { fieldName: 'website', label: 'Website', type: 'text', required: true, placeholder: 'https://acme.com' },
  { fieldName: 'phone', label: 'Business phone', type: 'text', required: true, placeholder: '+1 555 123 4567' },
];

/** Industry-specific question sets keyed by a normalized industry group. */
const BY_GROUP: Record<string, OnboardingQuestion[]> = {
  technology: [
    { fieldName: 'techStack', label: 'Tech stack', type: 'multiselect', options: ['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java', 'React', 'Node.js'], required: true },
    { fieldName: 'developers', label: 'Number of developers', type: 'number', required: true },
    { fieldName: 'devOps', label: 'Primary DevOps platform', type: 'select', options: ['AWS', 'GCP', 'Azure', 'Vercel', 'Self-hosted'], required: true },
  ],
  healthcare: [
    { fieldName: 'hipaaCompliant', label: 'HIPAA compliant?', type: 'boolean', required: true },
    { fieldName: 'patients', label: 'Approx. number of patients', type: 'number', required: true },
    { fieldName: 'specialties', label: 'Specialties', type: 'multiselect', options: ['Primary care', 'Cardiology', 'Dermatology', 'Pediatrics', 'Mental health', 'Telehealth'], required: true },
  ],
  retail: [
    { fieldName: 'stores', label: 'Number of stores', type: 'number', required: true },
    { fieldName: 'inventorySize', label: 'Inventory size (SKUs)', type: 'number', required: true },
    { fieldName: 'salesChannels', label: 'Sales channels', type: 'multiselect', options: ['In-store', 'Online', 'Marketplace', 'Wholesale'], required: true },
  ],
  manufacturing: [
    { fieldName: 'factories', label: 'Number of factories', type: 'number', required: true },
    { fieldName: 'capacity', label: 'Monthly production capacity', type: 'text', required: true, placeholder: 'e.g. 50,000 units' },
    { fieldName: 'certifications', label: 'Quality certifications', type: 'multiselect', options: ['ISO 9001', 'ISO 14001', 'AS9100', 'GMP', 'None'], required: true },
  ],
  finance: [
    { fieldName: 'compliance', label: 'Compliance frameworks', type: 'multiselect', options: ['SEC', 'GDPR', 'SOC 2', 'PCI-DSS', 'FINRA'], required: true },
    { fieldName: 'clients', label: 'Number of clients', type: 'number', required: true },
    { fieldName: 'portfolioSize', label: 'Assets under management', type: 'text', required: true, placeholder: 'e.g. $25M' },
  ],
};

/** Normalize a raw industry id/name to one of the question groups. */
function groupFor(industry: string): keyof typeof BY_GROUP | null {
  const s = industry.toLowerCase();
  if (/tech|information|software|scientific|ai|comput/.test(s)) return 'technology';
  if (/health|care|medical|hospital/.test(s)) return 'healthcare';
  if (/retail|trade|store|commerce|food|accommodation/.test(s)) return 'retail';
  if (/manufact|production|factory|industrial/.test(s)) return 'manufacturing';
  if (/financ|insurance|bank|capital|invest/.test(s)) return 'finance';
  return null;
}

/** Questions for an industry: common fields + any industry-specific set. */
export function getQuestionsForIndustry(industry: string | undefined): OnboardingQuestion[] {
  if (!industry) return COMMON;
  const group = groupFor(industry);
  return group ? [...COMMON, ...BY_GROUP[group]] : COMMON;
}
